/**
 * Session Management Context — SPA Architecture
 *
 * Manages user session entirely client-side:
 * 1. On mount: calls POST /auth/refresh to restore the session from the httpOnly cookie.
 * 2. Decodes the returned access token (JWT) to extract the session payload.
 * 3. Stores the access token in-memory via auth-fetch (never localStorage).
 * 4. Fetches the user profile after session is established.
 */

"use client"

import { Session } from "@/shared/types/session.type"
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from "react"
import { Profile } from "../types/auth-actions.type"
import { UserRole } from "@/shared/constants/user-roles"
import {
  clearAccessToken,
  getAccessToken,
  hasValidAccessToken,
  refreshOnce,
} from "@/shared/lib/auth-fetch"
import { getProfile, getVolunteerProfile } from "@/features/profile/api/profile.api"

const BASE_URL = (import.meta.env.VITE_BASE_BACKEND_URL || '/api') as string

/**
 * Decode JWT payload without verification (the server already verified it).
 * We only need the claims for UI routing / display.
 */
function decodeJwtPayload(token: string): Session | null {
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")))
    return payload as Session
  } catch {
    return null
  }
}

type SessionContextType = {
  session: Session | null
  setSession: (session: Session | null) => void
  profile: Profile | null
  setProfile: (profile: Profile | null) => void
  refreshSession: () => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
}

export const SessionContext = createContext<SessionContextType | null>(null)

export const SessionProvider = ({ children }: PropsWithChildren) => {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const initSession = useCallback(async () => {
    try {
      // Skip silent refresh during OAuth callback redirection to prevent premature token rotation and race conditions
      if (typeof window !== 'undefined' && window.location.pathname.includes('/auth/callback')) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)

      // A caller may request profile/session data after an action. Do not rotate
      // a healthy refresh token merely because an in-memory access token exists.
      const accessToken = hasValidAccessToken()
        ? getAccessToken()
        : await refreshOnce()

      if (!accessToken) {
        // No valid session — user is not logged in
        setSession(null)
        setProfile(null)
        setIsLoading(false)
        return
      }

      // Decode the JWT to get session claims
      const decoded = decodeJwtPayload(accessToken)
      if (!decoded) {
        setSession(null)
        setProfile(null)
        setIsLoading(false)
        return
      }

      setSession(decoded)

      // Fetch profile (blocking to ensure selectedRegion is loaded before redirects evaluate)
      try {
        const profileRes =
          decoded.role === UserRole.HOMEOWNER
            ? await getProfile(Number(decoded.sub))
            : await getVolunteerProfile(Number(decoded.sub))

        if (profileRes.success && profileRes.data) {
          setProfile(profileRes.data)
        }
      } catch {
        // Profile fetch failure is non-fatal
      } finally {
        setIsLoading(false)
      }
    } catch {
      setSession(null)
      setProfile(null)
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await fetch(`${BASE_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      })
    } catch {
      // Non-fatal
    }
    clearAccessToken()

    // The backend clears the HttpOnly refresh-token cookie. Clear any
    // historical client-side storage names that may remain from older builds.
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("aim-auth")
    localStorage.removeItem("googleSignupInfo")
    localStorage.removeItem("googleLoginFromLoginPage")
    localStorage.removeItem("selectedGoogleAccountEmail")
    localStorage.removeItem("reloadedAfterLogin")

    setSession(null)
    setProfile(null)
    // Let the AuthGuard handle the redirect to login
  }, [])

  useEffect(() => {
    initSession()
  }, [initSession])

  return (
    <SessionContext.Provider
      value={{
        session,
        setSession,
        profile,
        setProfile,
        refreshSession: initSession,
        logout,
        isLoading,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}

export const useSession = () => {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider")
  }
  return context
}